import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import { hexToRgb, darkenHex, lightenHex, mixHex, luminance, ensureContrastFloorDarken } from '../lib/theme/colorMath';
import { computeDarkBrandOverride } from '../lib/theme/darkBrandOverride';
import { documentTitleFor, TACIT_DEFAULT_TAGLINE } from '../lib/siteMeta';

const DEFAULT_FAVICON_HREF = '/vite.svg';

/**
 * Issue #786 — dashboard is always the default-branded home surface.
 * On dashboard routes we force the Tacit favicon and let consumers hide
 * their LogoHomeLink (circular nav).
 * Issue #1521 — /messenger is the learner home for messenger-enabled orgs;
 * it gets the same home-surface treatment (no circular logo link).
 */
function pathIsDashboard(pathname) {
  if (!pathname) return false;
  return pathname === '/dashboard' || pathname.startsWith('/dashboard/')
    || pathname === '/messenger' || pathname.startsWith('/messenger/');
}

/**
 * Subscribe to browser location changes without requiring <Router> context.
 * BrandingProvider is mounted above <Router> in App.jsx, so we cannot use
 * `useLocation`. Instead we patch history.pushState/replaceState and listen
 * to popstate to detect client-side navigation.
 */
function useBrowserPathname() {
  const [pathname, setPathname] = useState(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const update = () => setPathname(window.location.pathname);

    const { pushState, replaceState } = window.history;
    window.history.pushState = function patchedPushState(...args) {
      const result = pushState.apply(this, args);
      window.dispatchEvent(new Event('tacit:locationchange'));
      return result;
    };
    window.history.replaceState = function patchedReplaceState(...args) {
      const result = replaceState.apply(this, args);
      window.dispatchEvent(new Event('tacit:locationchange'));
      return result;
    };

    window.addEventListener('popstate', update);
    window.addEventListener('tacit:locationchange', update);

    return () => {
      // Issue #801: dev-only warning if another library re-patched after us.
      // Restoring our saved originals would silently clobber their patches.
      if (import.meta.env.DEV) {
        if (window.history.pushState.name !== 'patchedPushState') {
          console.warn('[BrandingContext] pushState was re-patched by another module — restore may clobber it');
        }
        if (window.history.replaceState.name !== 'patchedReplaceState') {
          console.warn('[BrandingContext] replaceState was re-patched by another module — restore may clobber it');
        }
      }
      window.history.pushState = pushState;
      window.history.replaceState = replaceState;
      window.removeEventListener('popstate', update);
      window.removeEventListener('tacit:locationchange', update);
    };
  }, []);

  return pathname;
}

const BrandingContext = createContext();

const TACIT_DEFAULTS = {
  app_name: 'Tacit',
  tagline: TACIT_DEFAULT_TAGLINE,
  primary_color: '#667eea',
  accent_color: '#764ba2',
  logo_url: null,        // LEGACY — single-variant slot, kept for back-compat (Praxis, LifeChoices)
  logo_url_light: null,  // Issue #1139 — preferred for light surfaces
  logo_url_dark: null,   // Issue #1139 — preferred for dark / branded surfaces
  favicon_url: null,
  onboarding_v2_enabled: false, // Issue #1256 — per-org kill-switch for the v2 learner walkthrough
  welcome_scenario_id: null,    // Issue #1256 — per-org override of the global is_onboarding scenario
  messenger_enabled: false,     // Issue #1521 — /messenger surface reachable for this org
  messenger_is_home: false,     // Issue #1627 — messenger replaces /dashboard as the learner home
  coach_display_name: null,     // Issue #1522 — per-org Coach DM cast name (null → generic label)
  platform_bot_name: null,      // Issue #1523 — per-org Platform-bot DM cast name (null → app_name)
  coach_dm_input_mode: 'off',   // Issue #1640 — Coach DM composer mode: 'open' | 'chips' | 'off' (initial render only; live value re-fetched per channel-messages poll)
};

export function useBranding() {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
}

export function BrandingProvider({ children }) {
  const { user } = useAuth();
  const { theme } = useTheme();
  const pathname = useBrowserPathname();
  const isDashboard = pathIsDashboard(pathname);

  const branding = useMemo(() => {
    const b = user?.branding || TACIT_DEFAULTS;
    // Issue #1139 — resolve light/dark with graceful fallback to legacy logo_url
    // so any existing branded org (Praxis, LifeChoices) that only has the single
    // logo_url field continues to render exactly as before.
    const logoLight = b.logo_url_light || b.logo_url || null;
    const logoDark = b.logo_url_dark || b.logo_url || null;
    return {
      // Issue #1630 — true when the org has no branding override at all
      // (running Tacit's own defaults). Gates the dark-mode precedence
      // contract: Tacit's hand-designed [data-theme='dark'] CSS block
      // should win, not a light-derived inline override.
      // Compared by VALUE, not object identity: auth's buildBranding()
      // always sends a fresh branding object, so `b === TACIT_DEFAULTS`
      // was never true for authenticated sessions and the dark palette
      // got overwritten by derived tokens (mega60 review fix, codex P1
      // + gemini convergent).
      isTacitDefault: !user?.branding || (
        (b.app_name || TACIT_DEFAULTS.app_name) === TACIT_DEFAULTS.app_name &&
        (b.primary_color || TACIT_DEFAULTS.primary_color) === TACIT_DEFAULTS.primary_color &&
        (b.accent_color || TACIT_DEFAULTS.accent_color) === TACIT_DEFAULTS.accent_color
      ),
      appName: b.app_name || TACIT_DEFAULTS.app_name,
      tagline: b.tagline || TACIT_DEFAULTS.tagline,
      primaryColor: b.primary_color || TACIT_DEFAULTS.primary_color,
      accentColor: b.accent_color || TACIT_DEFAULTS.accent_color,
      logoUrl: logoLight,        // LEGACY API — defaults to light variant
      logoUrlLight: logoLight,
      logoUrlDark: logoDark,
      faviconUrl: b.favicon_url || null,
      poweredBy: b.powered_by || null,
      isDashboard,
      // Issue #1256 — surface per-org kill-switch + welcome-scenario override.
      // Strict equality so any non-true value (undefined, null, "false") reads
      // as disabled — orgs opt in by setting the flag explicitly.
      onboardingV2Enabled: b.onboarding_v2_enabled === true,
      welcomeScenarioId: Number.isInteger(b.welcome_scenario_id) ? b.welcome_scenario_id : null,
      // Issue #1521/#1627 — strict-true messenger flags. `messengerEnabled` =
      // /messenger is reachable; `messengerIsHome` = messenger replaces the
      // dashboard as the learner home (and force-disables onboarding-v2; the
      // server computes the effective onboarding flag = onboarding && !is_home,
      // so onboarding_v2 and messengerIsHome can never both be true on the wire).
      messengerEnabled: b.messenger_enabled === true,
      messengerIsHome: b.messenger_is_home === true,
      // Issue #1522/#1523 — per-org messenger DM cast names (null → the UI
      // falls back to a generic label / app_name). Never normalise per-org.
      coachDisplayName: b.coach_display_name || null,
      platformBotName: b.platform_bot_name || null,
      // Issue #1640 — strict-match; anything but 'open'/'chips' reads as 'off'
      // (the initial-render value; ChannelView re-fetches the live value per
      // channel-messages poll for kill-switch liveness without a reload).
      coachDmInputMode: b.coach_dm_input_mode === 'open' || b.coach_dm_input_mode === 'chips'
        ? b.coach_dm_input_mode
        : 'off',
    };
  }, [user?.branding, isDashboard]);

  // Apply CSS custom properties, theme attribute, and document title
  useEffect(() => {
    const root = document.documentElement;
    // Set theme attribute for CSS custom property selectors (Issue #603).
    // ThemeProvider (mounted above BrandingProvider) normally sets this
    // before this effect runs; this is just a defensive fallback for the
    // (untested-in-prod) case of BrandingProvider being used standalone.
    if (!root.getAttribute('data-theme')) {
      root.setAttribute('data-theme', 'light');
    }

    const BRAND_OVERRIDE_PROPS = [
      '--brand-primary',
      '--brand-primary-rgb',
      '--brand-primary-hover',
      '--brand-primary-light',
      '--brand-accent',
      '--brand-accent-soft',
      // #4333: light-mode small-text/link colour derived from the brand fill.
      // Dark mode never uses it (theme-variables.css points --color-text-link
      // at the dark brand there), so every dark path clears it.
      '--brand-primary-text',
    ];

    if (theme === 'dark') {
      // Issue #1630 — precedence contract: base tokens → theme-mode block →
      // org override → derived. computeDarkBrandOverride decides whether to
      // clear inline overrides (Tacit — let the CSS dark block win) or set
      // explicit ones (Praxis curated, or an auto-derived variant for any
      // other branded org).
      const decision = computeDarkBrandOverride({
        isTacitDefault: branding.isTacitDefault,
        appName: branding.appName,
        primaryColor: branding.primaryColor,
        accentColor: branding.accentColor,
      });
      if (decision.mode === 'skip') {
        BRAND_OVERRIDE_PROPS.forEach((prop) => root.style.removeProperty(prop));
      } else {
        Object.entries(decision.tokens).forEach(([prop, value]) => {
          root.style.setProperty(prop, value);
        });
        // The override sets only the tokens it derives; a light-mode
        // --brand-primary-text left on the root would be stale.
        root.style.removeProperty('--brand-primary-text');
      }
    } else {
      // Light mode (default) — unchanged from pre-#1630 behavior: always
      // derive inline overrides from the org's light primary/accent colors.
      root.style.setProperty('--brand-primary', branding.primaryColor);
      root.style.setProperty('--brand-accent', branding.accentColor);

      // Issue #689: Set derived brand tokens so all brand-tinted surfaces respond to org branding
      const [r, g, b] = hexToRgb(branding.primaryColor);
      root.style.setProperty('--brand-primary-rgb', `${r}, ${g}, ${b}`);
      root.style.setProperty('--brand-primary-hover', darkenHex(branding.primaryColor, 0.12));
      root.style.setProperty('--brand-primary-light', lightenHex(branding.primaryColor, 0.35));
      // #4333 (D-337 option a): small text and links need 4.5:1 on white, the
      // brand fill often does not (#667eea is 3.66:1). Fills keep --brand-primary.
      root.style.setProperty('--brand-primary-text', ensureContrastFloorDarken(branding.primaryColor, '#ffffff', 4.5));

      // Soften accent for hero gradients when accent is very dark (e.g. #000000)
      const accentLum = luminance(branding.accentColor);
      const softAccent = accentLum < 0.05
        ? mixHex(branding.primaryColor, branding.accentColor, 0.3)
        : branding.accentColor;
      root.style.setProperty('--brand-accent-soft', softAccent);
    }

    // #3756: Tacit's own surfaces use the public site title (kept identical to
    // the static index.html <title> so JS-running crawlers see the same head
    // as non-JS ones). White-labelled orgs keep their own app name + tagline.
    document.title = documentTitleFor(branding);

    // Issue #786: favicon swap
    // - On /dashboard routes always show the default Tacit favicon so the
    //   dashboard reads as the canonical home surface regardless of which
    //   org-branded URL brought the user there.
    // - Elsewhere, use the org favicon when provided, otherwise fall back
    //   to the default.
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const shouldUseOrgFavicon = !isDashboard && Boolean(branding.faviconUrl);
    link.href = shouldUseOrgFavicon ? branding.faviconUrl : DEFAULT_FAVICON_HREF;
  }, [branding, isDashboard, theme]);

  return (
    <BrandingContext.Provider value={branding}>
      {children}
    </BrandingContext.Provider>
  );
}
